import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Building2, FileBarChart, Plus, Trash2, Upload } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "../../api/client";
import { avvisoLettura } from "../../utils/lettureOdoo";
import * as clientsApi from "../../api/clients";
import { Cliente, SedeCliente } from "../../api/clients";
import type { RiepilogoUtentiConti } from "../../api/utentiCassaforte";
import { ExcelUtenti } from "../Sedi/ExcelUtenti";
import * as anagraficaApi from "../../api/anagrafica";
import { CapacitaOperatore, CentroAssistenza, IstitutoVigilanza, OperatoreLogistico } from "../../api/anagrafica";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { ImportModal } from "../../components/ui/ImportModal";
import { SchedaClienteAnteprima } from "../../components/ui/SchedaClienteAnteprima";
import { MediaUploader } from "../../components/ui/MediaUploader";
import { ExportButton } from "../../components/ui/ExportButton";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { InviaReportModal } from "../../components/ui/InviaReportModal";
import * as reportApi from "../../api/report";
import * as filesApi from "../../api/files";
import { DataStorico } from "../../components/ui/DataStorico";
import { isFutura } from "../../utils/dataIpotesi";
import { useOrdinamentoUrl, useRicercaUrl, useScriviParametri, useTornaIndietro } from "../../hooks/useStatoUrl";
import { LinkIndietro } from "../../components/ui/LinkIndietro";

type AnagraficheTab = "clienti" | "centri" | "istituti" | "operatori";
const TAB_ANAGRAFICHE: AnagraficheTab[] = ["clienti", "centri", "istituti", "operatori"];

// ---------------------------------------------------------------------------
// Elenco sedi: riepilogo «Utenti autorizzati e conti» (GET /clienti/:id/sedi)
// ---------------------------------------------------------------------------

type FiltroUtentiConti = "tutte" | "senza_conto" | "senza_utenze" | "con_avviso";

/**
 * «Senza conto tecnico» = almeno una cassaforte della sede senza conto: una
 * sede senza casseforti non ha dove metterlo, e non è lavoro da fare.
 */
const FILTRI_UTENTI_CONTI: Record<FiltroUtentiConti, { etichetta: string; tiene: (r: RiepilogoUtentiConti | undefined) => boolean }> = {
  tutte: { etichetta: "Tutte le sedi", tiene: () => true },
  senza_conto: { etichetta: "Senza conto tecnico", tiene: (r) => !!r && r.casseforti > r.casseforti_con_conto },
  senza_utenze: { etichetta: "Senza utenze", tiene: (r) => !r || r.utenze === 0 },
  con_avviso: { etichetta: "Con avviso sui conti", tiene: (r) => !!r && (r.conto_condiviso || r.piu_conti) },
};

function BadgeContoTecnico({ r }: { r: RiepilogoUtentiConti | undefined }) {
  if (!r || r.casseforti === 0) return <span className="text-ink/40" title="Nessuna cassaforte collocata qui">—</span>;
  if (r.casseforti_con_conto === r.casseforti) return <Badge color="success">Sì</Badge>;
  if (r.casseforti_con_conto === 0) return <Badge color="danger">No</Badge>;
  return (
    <span title="Casseforti con conto tecnico sul totale">
      <Badge color="warning">
        {r.casseforti_con_conto}/{r.casseforti}
      </Badge>
    </span>
  );
}

function BadgeAccredito({ r }: { r: RiepilogoUtentiConti | undefined }) {
  if (!r) return null;
  if (r.accredito === "iban") return <Badge color="success">IBAN</Badge>;
  if (r.accredito === "conto_interno") return <Badge color="success">Conto interno</Badge>;
  if (r.accredito === "ereditato") return <span className="text-ink/60">Dall&apos;azienda</span>;
  return <Badge color="danger">Manca</Badge>;
}

/**
 * Anagrafiche: elenco in `/clients`, scheda cliente in `/clients/:id`.
 *
 * La scheda ha un URL suo perché da lì si apre una sede (`/sedi/:id`), e
 * "Indietro" dalla sede deve riportare al cliente, non all'elenco. Tab
 * (`?tab=`), ricerca (`?ricerca=`) e ordinamento stanno nell'URL dell'elenco,
 * così tornando dalla scheda lo si ritrova com'era. Vedi `hooks/useStatoUrl.ts`.
 */
export default function Clients() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const torna = useTornaIndietro();
  const { id: clienteIdUrl } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();
  const tabUrl = searchParams.get("tab") ?? "";
  const tab: AnagraficheTab = (TAB_ANAGRAFICHE as string[]).includes(tabUrl) ? (tabUrl as AnagraficheTab) : "clienti";
  // L'ordinamento è della tabella della tab che si lascia: le colonne
  // dell'altra sono diverse.
  const setTab = (t: AnagraficheTab) =>
    scrivi({ tab: t === "clienti" ? null : t, elenco: null, ordina: null, verso: null });

  const [clienti, setClienti] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingPull, setIsSyncingPull] = useState(false);
  const [doppioniResidui, setDoppioniResidui] = useState<
    { odoo_partner_id: number; nome: string; cliente_id: string }[]
  >([]);
  /** Rumore in ingresso da ripulire in Odoo — vedi il pannello omonimo piu' sotto. */
  const [contattiProva, setContattiProva] = useState<{ odoo_partner_id: number; nome: string }[]>([]);
  const [gusciVuoti, setGusciVuoti] = useState<{ odoo_partner_id: number; nome: string }[]>([]);
  const [isSyncingPush, setIsSyncingPush] = useState(false);
  const { testo: search, setTesto: setSearch, valore: ricercaAttiva } = useRicercaUrl();

  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  /** La scheda in `/clients/:id` non c'è (404) o non si è potuta leggere. */
  const [clienteNonTrovato, setClienteNonTrovato] = useState(false);
  /** Pannello del Report cliente: periodo, scarico e invio. */
  const [reportAperto, setReportAperto] = useState(false);
  const [reportDa, setReportDa] = useState("");
  const [reportA, setReportA] = useState("");
  const [sedi, setSedi] = useState<SedeCliente[]>([]);
  const [sediLoading, setSediLoading] = useState(false);
  const [filtroUtentiConti, setFiltroUtentiConti] = useState<FiltroUtentiConti>("tutte");

  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [sedeModalOpen, setSedeModalOpen] = useState(false);
  const [editingSede, setEditingSede] = useState<SedeCliente | null>(null);
  const [importClientiOpen, setImportClientiOpen] = useState(false);
  // La Scheda Cliente ha un percorso suo: non è un formato tabellare (una riga
  // = un cliente) ma un modulo verticale, e non crea clienti — completa quelli
  // che ci sono già. Vedi `SchedaClienteAnteprima`.
  const [schedaOpen, setSchedaOpen] = useState(false);
  const [importSediOpen, setImportSediOpen] = useState(false);

  /**
   * Numero dell'ultima richiesta partita. Le risposte non arrivano per forza
   * nell'ordine in cui le richieste sono partite: senza questo controllo, la
   * risposta lenta di una ricerca vecchia ("b") poteva arrivare dopo quella di
   * "bkno" e rimettere in tabella i risultati di "b".
   */
  const ultimaRichiesta = useRef(0);

  async function loadClienti(searchValue = ricercaAttiva) {
    const richiesta = ++ultimaRichiesta.current;
    setIsLoading(true);
    try {
      const { clienti: list } = await clientsApi.listClienti(searchValue || undefined);
      if (richiesta === ultimaRichiesta.current) setClienti(list);
    } catch (err) {
      if (richiesta === ultimaRichiesta.current) showToast(getErrorMessage(err, "Impossibile caricare i clienti."), "error");
    } finally {
      if (richiesta === ultimaRichiesta.current) setIsLoading(false);
    }
  }

  async function handleSyncPull() {
    setIsSyncingPull(true);
    try {
      const result = await clientsApi.pullClientiFromOdoo();
      // Se la lettura era piena o quasi, l'avviso viene PRIMA dei conteggi:
      // "480 sincronizzati" è vero e allo stesso tempo fuorviante quando Odoo
      // ne ha lasciati fuori, e chi legge si ferma alla prima riga.
      const avviso = avvisoLettura(result.lettura);
      showToast(
        (avviso ? `${avviso.testo} · ` : "") +
          `${result.synced} aziende madri, ${result.sedi_synced} sedi sincronizzate` +
          (result.sedi_senza_genitore > 0 ? ` · ${result.sedi_senza_genitore} punti vendita senza azienda madre trovata` : "") +
          (result.doppioni_residui.length > 0 ? ` · ${result.doppioni_residui.length} doppioni da un vecchio sync da ripulire` : "") +
          (result.contatti_prova.length > 0 ? ` · ${result.contatti_prova.length} contatti di prova da ripulire in Odoo` : ""),
        // I gusci vuoti NON entrano nel messaggio e non colorano l'avviso, pur
        // essendo il numero piu' grande dei quattro (425 su 480 in sviluppo).
        // Un cliente appena acquisito e' un guscio vuoto: se ogni sync uscisse
        // arancione per quel motivo, l'avviso smetterebbe di voler dire
        // qualcosa proprio mentre gli altri tre continuano a volerlo dire.
        // Restano nel pannello qui sotto, dove c'e' lo spazio per spiegarlo.
        avviso ||
          result.doppioni_residui.length > 0 ||
          result.sedi_senza_genitore > 0 ||
          result.contatti_prova.length > 0
          ? "warning"
          : "success"
      );
      setDoppioniResidui(result.doppioni_residui);
      setContattiProva(result.contatti_prova);
      setGusciVuoti(result.gusci_vuoti);
      loadClienti();
    } catch (err) {
      showToast(getErrorMessage(err, "Lettura da Odoo non riuscita."), "error");
    } finally {
      setIsSyncingPull(false);
    }
  }

  async function handleDeleteDoppione(clienteId: string) {
    const confirmed = window.confirm("Eliminare questo cliente doppione? L'operazione è bloccata automaticamente se ha sedi o altri dati collegati.");
    if (!confirmed) return;
    try {
      await clientsApi.deleteCliente(clienteId);
      showToast("Doppione eliminato.", "success");
      setDoppioniResidui((prev) => prev.filter((d) => d.cliente_id !== clienteId));
      loadClienti();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare — probabilmente ha dati collegati."), "error");
    }
  }

  async function handleDeleteCliente() {
    if (!selectedCliente) return;
    const confirmed = window.confirm(
      `Eliminare "${selectedCliente.nome}"? L'operazione è bloccata automaticamente se ha sedi, installazioni, sopralluoghi o attivazioni collegate.`
    );
    if (!confirmed) return;
    try {
      await clientsApi.deleteCliente(selectedCliente.id);
      showToast("Cliente eliminato.", "success");
      // `replace`: la scheda di un cliente eliminato non è più una pagina a
      // cui tornare.
      torna("/clients", { replace: true });
      loadClienti();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare — verifica che non abbia dati collegati."), "error");
    }
  }

  async function handleConverti(tipo: "centro" | "istituto") {
    if (!selectedCliente || !tipo) return;
    const etichetta = tipo === "centro" ? "Centro Assistenza" : "Istituto di Vigilanza";
    const confirmed = window.confirm(
      `Creare un nuovo ${etichetta} a partire dai dati di "${selectedCliente.nome}"? Il Cliente originale NON verrà toccato né eliminato — resteranno due record separati.`
    );
    if (!confirmed) return;
    try {
      if (tipo === "centro") {
        await clientsApi.convertiInCentroAssistenza(selectedCliente.id);
      } else {
        await clientsApi.convertiInIstitutoVigilanza(selectedCliente.id);
      }
      showToast(`${etichetta} creato dai dati di questo contatto.`, "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Conversione non riuscita."), "error");
    }
  }


  async function handleSyncPush() {
    if (!selectedCliente) return;
    setIsSyncingPush(true);
    try {
      const { cliente } = await clientsApi.pushClienteToOdoo(selectedCliente.id);
      setSelectedCliente(cliente);
      showToast("Inviato a Odoo Contacts.", "success");
      loadClienti();
    } catch (err) {
      showToast(getErrorMessage(err, "Invio a Odoo non riuscito."), "error");
    } finally {
      setIsSyncingPush(false);
    }
  }

  useEffect(() => {
    loadClienti(ricercaAttiva);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricercaAttiva]);

  async function caricaSedi(clienteId: string) {
    setSediLoading(true);
    try {
      const { sedi: list } = await clientsApi.listSediByCliente(clienteId);
      setSedi(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare le sedi."), "error");
    } finally {
      setSediLoading(false);
    }
  }

  // La scheda segue l'URL: aprirla è navigare a `/clients/:id` (una voce di
  // cronologia), e qui la si carica. Dall'elenco il cliente c'è già e si mostra
  // subito; da un link o tornando da una sede lo si legge per id.
  useEffect(() => {
    setClienteNonTrovato(false);
    if (!clienteIdUrl) {
      setSelectedCliente(null);
      setSedi([]);
      return;
    }
    let annullato = false;
    const giaInElenco = clienti.find((c) => c.id === clienteIdUrl);
    setSelectedCliente(giaInElenco ?? null);
    setSedi([]);
    if (!giaInElenco) {
      clientsApi
        .getCliente(clienteIdUrl)
        .then(({ cliente }) => {
          if (!annullato) setSelectedCliente(cliente);
        })
        .catch(() => {
          if (!annullato) setClienteNonTrovato(true);
        });
    }
    caricaSedi(clienteIdUrl);
    return () => {
      annullato = true;
    };
    // `clienti` fuori di proposito: ricaricare l'elenco non deve ricaricare la
    // scheda aperta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteIdUrl]);

  function openCliente(cliente: Cliente) {
    navigate(`/clients/${cliente.id}`);
  }

  const clienteColumns: TableColumn<Cliente>[] = [
    // Nome e Sedi non si spengono: il primo identifica la riga, il secondo e'
    // l'aggancio alla navigazione verso i punti vendita. Una tabella clienti
    // senza nome o senza il conteggio sedi non risponde piu' a niente.
    { key: "nome", header: "Nome", sempreVisibile: true, sortValue: (c) => c.nome },
    { key: "insegna", header: "Insegna", sortValue: (c) => c.insegna, render: (c) => c.insegna ?? "—" },
    { key: "partita_iva", header: "P. IVA", sortValue: (c) => c.partita_iva, render: (c) => c.partita_iva ?? "—" },
    {
      key: "referente_nome",
      header: "Referente",
      // SPENTA DI DEFAULT perche' e' vuota su tutte e 480 le righe del database
      // di SVILUPPO: `referente_nome` e' popolato allo 0%, quindi oggi mostra un
      // trattino su ogni riga e occupa spazio senza dire niente.
      //
      // NON e' una constatazione sulla produzione, dove non posso guardare: se
      // la' e' popolata, va riaccesa dal menu e questa riga va tolta. La
      // verifica e' in carico a Fabio (15/08/2026) — finche' non arriva, la
      // colonna resta disponibile, solo non imposta a chi apre la pagina.
      nascostaDiDefault: true,
      sortValue: (c) => c.referente_nome,
      render: (c) => c.referente_nome ?? "—",
    },
    { key: "sedi", header: "Sedi", sempreVisibile: true, sortValue: (c) => c._count?.sedi ?? 0, render: (c) => c._count?.sedi ?? 0 },

    // --- Spente di default: servono a un lavoro specifico, non a tutti -------
    { key: "comune", header: "Comune", nascostaDiDefault: true, sortValue: (c) => c.comune, render: (c) => c.comune ?? "—" },
    { key: "email", header: "Email", nascostaDiDefault: true, sortValue: (c) => c.email, render: (c) => c.email ?? "—" },
    { key: "telefono", header: "Telefono", nascostaDiDefault: true, sortValue: (c) => c.telefono, render: (c) => c.telefono ?? "—" },
    {
      key: "odoo",
      header: "Da Odoo",
      nascostaDiDefault: true,
      // Si ordina sul booleano reso testo, cosi' l'ordinamento raggruppa invece
      // di sparpagliare: tutti i "Si" da una parte, tutti i "No" dall'altra.
      sortValue: (c) => (c.odoo_partner_id ? "Si" : "No"),
      render: (c) =>
        c.odoo_partner_id ? "Si" : <span className="text-ink/40">No</span>,
    },
  ];

  const sedeColumns: TableColumn<SedeCliente>[] = [
    // Sede e Storico non si spengono: il nome identifica la riga, "Storico" e'
    // l'unica azione della tabella.
    { key: "nome_sede", header: "Sede", sempreVisibile: true, sortValue: (s) => s.nome_sede, render: (s) => s.nome_sede ?? "—" },
    { key: "indirizzo", header: "Indirizzo", sortValue: (s) => s.indirizzo, render: (s) => s.indirizzo ?? "—" },
    {
      key: "citta",
      header: "Città",
      // Si ordina sulla sola citta', non sulla stringa "Citta' (PR)" che si
      // vede: la provincia in coda cambierebbe l'ordine a parita' di comune.
      sortValue: (s) => s.citta,
      render: (s) => (s.citta ? `${s.citta}${s.provincia ? ` (${s.provincia})` : ""}` : "—"),
    },
    { key: "telefono", header: "Telefono", sortValue: (s) => s.telefono, render: (s) => s.telefono ?? "—" },

    // --- Utenti autorizzati e conti (riepilogo calcolato dal server) ---------
    {
      key: "utenze",
      header: "Utenze",
      sortValue: (s) => s.utenti_conti?.utenze ?? 0,
      render: (s) => (s.utenti_conti?.utenze ? s.utenti_conti.utenze : <span className="text-ink/40">0</span>),
    },
    {
      key: "conto_tecnico",
      header: "Conto tecnico",
      sortValue: (s) => (s.utenti_conti ? s.utenti_conti.casseforti_con_conto - s.utenti_conti.casseforti : 0),
      render: (s) => <BadgeContoTecnico r={s.utenti_conti} />,
    },
    {
      key: "accredito",
      header: "Accredito",
      sortValue: (s) => s.utenti_conti?.accredito ?? "",
      render: (s) => <BadgeAccredito r={s.utenti_conti} />,
    },
    {
      key: "avviso_conti",
      header: "Avviso",
      sortValue: (s) => (s.utenti_conti && (s.utenti_conti.conto_condiviso || s.utenti_conti.piu_conti) ? "Si" : "No"),
      render: (s) =>
        s.utenti_conti?.conto_condiviso || s.utenti_conti?.piu_conti ? (
          <span className="flex flex-wrap gap-1">
            {s.utenti_conti.conto_condiviso && <Badge color="warning">Conto condiviso</Badge>}
            {s.utenti_conti.piu_conti && <Badge color="warning">Più conti</Badge>}
          </span>
        ) : (
          ""
        ),
    },

    // --- Spente di default --------------------------------------------------
    { key: "cap", header: "CAP", nascostaDiDefault: true, sortValue: (s) => s.cap, render: (s) => s.cap ?? "—" },
    { key: "email", header: "Email", nascostaDiDefault: true, sortValue: (s) => s.email, render: (s) => s.email ?? "—" },
    {
      key: "geolocalizzata",
      header: "Posizionata",
      nascostaDiDefault: true,
      // VA CONTRO il criterio del riempimento, ed e' voluto: 219 sedi su 229
      // non hanno coordinate. Una colonna quasi tutta vuota di solito non serve;
      // questa serve PROPRIO perche' e' vuota — accesa e ordinata isola in cima
      // il lavoro che manca, che oggi si vede solo aprendo la mappa.
      sortValue: (s) => (s.lat !== null && s.lng !== null ? "Si" : "No"),
      render: (s) =>
        s.lat !== null && s.lng !== null ? (
          <span title={s.geo_precisione ?? undefined}>Si</span>
        ) : (
          <span className="text-ink/40">No</span>
        ),
    },
    {
      key: "odoo",
      header: "Da Odoo",
      nascostaDiDefault: true,
      sortValue: (s) => (s.odoo_partner_id ? "Si" : "No"),
      render: (s) => (s.odoo_partner_id ? "Si" : <span className="text-ink/40">No</span>),
    },
    {
      key: "storico",
      header: "",
      // `sempreVisibile` anche perche' l'intestazione e' vuota: nel menu
      // sarebbe una casella senza etichetta, impossibile da riaccendere.
      sempreVisibile: true,
      // Il click sulla riga apre già la modifica della sede: questo link deve
      // fermare la propagazione, altrimenti aprirebbe anche quella dietro.
      render: (s) => (
        <Link
          to={`/sedi/${s.id}`}
          onClick={(e) => e.stopPropagation()}
          className="whitespace-nowrap text-xs font-semibold text-primary hover:underline"
        >
          Storico →
        </Link>
      ),
    },
  ];

  // ============================================================================
  // Vista dettaglio cliente → sedi
  // ============================================================================
  if (clienteIdUrl && !selectedCliente) {
    return clienteNonTrovato ? (
      <div className="py-12 text-center">
        <p className="text-sm text-ink/60">Cliente non trovato.</p>
        <LinkIndietro to="/clients" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Torna alle Anagrafiche
        </LinkIndietro>
      </div>
    ) : (
      <p className="py-12 text-center text-sm text-ink/50">Caricamento…</p>
    );
  }

  if (clienteIdUrl && selectedCliente) {
    return (
      <div>
        <LinkIndietro to="/clients" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink/60 hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Tutti i clienti
        </LinkIndietro>

        <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-card bg-primary/10 text-primary">
              <Building2 className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-ink">{selectedCliente.nome}</h1>
              <p className="text-sm text-ink/50">
                {selectedCliente.partita_iva ? `P. IVA ${selectedCliente.partita_iva} · ` : ""}
                {selectedCliente.referente_nome ?? "Nessun referente indicato"}
              </p>
              {(selectedCliente.indirizzo || selectedCliente.comune) && (
                <p className="text-sm text-ink/50">
                  {[selectedCliente.indirizzo, selectedCliente.civico, selectedCliente.cap, selectedCliente.comune]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Solo con `report:invia`: il report cliente mette insieme quattro
                moduli, e generarlo è la stessa azione che mandarlo meno l'email. */}
            {hasPermission("report:invia") && (
              <Button variant="secondary" onClick={() => setReportAperto(true)}>
                <FileBarChart className="h-4 w-4" aria-hidden="true" />
                Report
              </Button>
            )}
            <Button variant="ghost" onClick={handleDeleteCliente}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina cliente
            </Button>
            <select
              value=""
              onChange={(e) => handleConverti(e.target.value as "centro" | "istituto")}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            >
              <option value="">Converti in…</option>
              <option value="centro">Centro Assistenza</option>
              <option value="istituto">Istituto di Vigilanza</option>
            </select>
            <Button variant="secondary" onClick={() => { setEditingCliente(selectedCliente); setClienteModalOpen(true); }}>
              Modifica cliente
            </Button>
            <Button onClick={() => { setEditingSede(null); setSedeModalOpen(true); }}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuova sede
            </Button>
          </div>
        </header>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label htmlFor="filtro-utenti-conti" className="text-sm text-ink/60">
            Utenti e conti
          </label>
          <select
            id="filtro-utenti-conti"
            value={filtroUtentiConti}
            onChange={(e) => setFiltroUtentiConti(e.target.value as FiltroUtentiConti)}
            className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            {(Object.keys(FILTRI_UTENTI_CONTI) as FiltroUtentiConti[]).map((f) => (
              <option key={f} value={f}>
                {FILTRI_UTENTI_CONTI[f].etichetta}
              </option>
            ))}
          </select>
          {filtroUtentiConti !== "tutte" && (
            <span className="text-xs text-ink/45">
              {sedi.filter((s) => FILTRI_UTENTI_CONTI[filtroUtentiConti].tiene(s.utenti_conti)).length} di {sedi.length}
            </span>
          )}
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ink/60">Elenco utenti delle casseforti: un foglio Excel per sede.</p>
          <ExcelUtenti
            ambito="cliente"
            id={selectedCliente.id}
            puoCaricare={hasPermission("clienti_sedi:update")}
            onApplicato={() => caricaSedi(selectedCliente.id)}
          />
        </div>

        <Table
          columns={sedeColumns}
          ordinabile
          ordine={ordine}
          onOrdineChange={onOrdineChange}
          sceltaColonne="sedi"
          data={sedi.filter((s) => FILTRI_UTENTI_CONTI[filtroUtentiConti].tiene(s.utenti_conti))}
          rowKey={(s) => s.id}
          isLoading={sediLoading}
          emptyMessage={filtroUtentiConti === "tutte" ? "Nessuna sede registrata per questo cliente." : "Nessuna sede con questo filtro."}
          onRowClick={(s) => { setEditingSede(s); setSedeModalOpen(true); }}
        />

        <ClienteAttivitaPanel clienteId={selectedCliente.id} />

        <div className="mt-6 rounded-card border border-line bg-white p-4 shadow-card">
          <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Odoo Contacts</p>
          <div className="flex items-center justify-between">
            <p className="text-xs text-ink/50">
              {selectedCliente.odoo_partner_id
                ? `Collegato al contatto Odoo #${selectedCliente.odoo_partner_id}${
                    selectedCliente.odoo_last_sync
                      ? ` · ultimo invio ${new Date(selectedCliente.odoo_last_sync).toLocaleString("it-IT")}`
                      : ""
                  }`
                : "Non ancora collegato a nessun contatto Odoo."}
            </p>
            <Button size="sm" variant="secondary" loading={isSyncingPush} onClick={handleSyncPush}>
              {selectedCliente.odoo_partner_id ? "Aggiorna su Odoo" : "Invia a Odoo"}
            </Button>
          </div>
        </div>

        <div className="mt-6 rounded-card border border-line bg-white p-4 shadow-card">
          <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">
            Foto e documenti <span className="font-normal normal-case text-ink/40">— es. copia contratto, foto negozio</span>
          </p>
          <MediaUploader entityType="cliente" entityId={selectedCliente.id} />
        </div>

        <DocumentiClientePanel clienteId={selectedCliente.id} />

        <ClienteModal
          open={clienteModalOpen}
          onClose={() => setClienteModalOpen(false)}
          cliente={editingCliente}
          onSaved={(updated) => {
            setSelectedCliente(updated);
            loadClienti();
          }}
        />

        <SedeModal
          open={sedeModalOpen}
          onClose={() => setSedeModalOpen(false)}
          clienteId={selectedCliente.id}
          sede={editingSede}
          onSaved={() => {
            caricaSedi(selectedCliente.id);
            loadClienti();
          }}
        />

        <InviaReportModal
          open={reportAperto}
          onClose={() => setReportAperto(false)}
          titolo={`Report cliente — ${selectedCliente.nome}`}
          nomeAllegato={reportApi.nomeFileReport("report-cliente", selectedCliente.nome)}
          caricaDestinatari={() => reportApi.destinatariPropostiReport("cliente", selectedCliente!.id)}
          scarica={() =>
            reportApi.scaricaReport(
              "cliente",
              selectedCliente!.id,
              reportApi.nomeFileReport("report-cliente", selectedCliente!.nome),
              { da: reportDa, a: reportA }
            )
          }
          invia={(destinatari, nota) =>
            reportApi.inviaReport("cliente", selectedCliente!.id, destinatari, nota, { da: reportDa, a: reportA })
          }
        >
          <div>
            <p className="mb-1 text-sm font-medium text-ink">Periodo</p>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Dal" type="date" value={reportDa} onChange={(e) => setReportDa(e.target.value)} />
              <Input label="Al" type="date" value={reportA} onChange={(e) => setReportA(e.target.value)} />
            </div>
            {/* Lasciare le date vuote non è un errore da segnalare: è la scelta
                "tutto lo storico", ed è quella che serve la prima volta che si
                guarda un cliente. */}
            <p className="mt-1 text-xs text-ink/50">
              Lascia vuoto per includere tutto lo storico. I record senza data restano fuori dal periodo e vengono
              contati nella sezione Totali.
            </p>
          </div>
        </InviaReportModal>
      </div>
    );
  }

  // ============================================================================
  // Vista lista clienti
  // ============================================================================
  return (
    <div>
      <div className="mb-6 flex gap-1.5 rounded-lg bg-black/5 p-1 w-fit">
        {(
          [
            ["clienti", "Clienti e Sedi"],
            ["centri", "Centri Assistenza"],
            ["istituti", "Istituti di Vigilanza"],
            ["operatori", "Operatori logistici"],
          ] as [AnagraficheTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
              tab === key ? "bg-white text-ink shadow-sm" : "text-ink/50 hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "centri" && <CentriAssistenzaTab />}
      {tab === "istituti" && <IstitutiVigilanzaTab />}
      {tab === "operatori" && <OperatoriLogisticiTab />}

      {tab === "clienti" && (
      <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Clienti e Sedi</h1>
          <p className="mt-1 text-sm text-ink/50">Anagrafica clienti e punti vendita.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={isSyncingPull} onClick={handleSyncPull}>
            Sincronizza da Odoo
          </Button>
          <Button variant="secondary" onClick={() => setImportSediOpen(true)}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            Importa sedi
          </Button>
          <Button variant="secondary" onClick={() => setImportClientiOpen(true)}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            Importa clienti
          </Button>
          <Button variant="secondary" onClick={() => setSchedaOpen(true)}>
            <Upload className="h-4 w-4" aria-hidden="true" />
            Carica Scheda Cliente
          </Button>
          <ExportButton
            endpoint="/clienti/export"
            filename="clienti.xlsx"
            fields={[{ key: "search", label: "Cerca per nome o P.IVA", type: "text" }]}
          />
          <Button onClick={() => { setEditingCliente(null); setClienteModalOpen(true); }}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nuovo cliente
          </Button>
        </div>
      </header>

      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Cerca per nome o P. IVA…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {doppioniResidui.length > 0 && (
        <div className="mb-4 rounded-card border border-accent/30 bg-accent/5 p-4">
          <p className="mb-2 text-sm font-bold text-ink">
            {doppioniResidui.length} punti vendita risultano ancora come clienti a sé stanti
          </p>
          <p className="mb-3 text-xs text-ink/60">
            Creati da una sincronizzazione precedente, prima che riconoscesse i punti vendita come sedi
            dell'azienda madre. Puoi eliminarli qui — l'eliminazione è bloccata automaticamente se hanno già
            sedi, installazioni, sopralluoghi o attivazioni collegate.
          </p>
          <div className="flex flex-wrap gap-2">
            {doppioniResidui.map((d) => (
              <span
                key={d.cliente_id}
                className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-ink shadow-sm"
              >
                {d.nome}
                <button
                  type="button"
                  onClick={() => handleDeleteDoppione(d.cliente_id)}
                  className="text-accent hover:underline"
                >
                  Elimina
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {(contattiProva.length > 0 || gusciVuoti.length > 0) && (
        <div className="mb-4 rounded-card border border-line bg-white p-4">
          <p className="mb-1 text-sm font-bold text-ink">Da ripulire in Odoo</p>
          <p className="mb-1 text-xs text-ink/60">
            Contatti Odoo, non record di questa app: eliminarli da qui non servirebbe, la sincronizzazione
            successiva li riporterebbe indietro. Il numero accanto al nome è l'id del contatto in Odoo.
          </p>
          <ElencoDaRipulire
            titolo={`${contattiProva.length} contatti di prova`}
            descrizione="Il nome contiene «test», «prova» o «demo» come parola a sé stante."
            voci={contattiProva}
          />
          <ElencoDaRipulire
            titolo={`${gusciVuoti.length} clienti senza nessun dato collegato`}
            descrizione="Nessuna sede, installazione, dispositivo, sopralluogo, attivazione o intervento. Non sono per forza da buttare: un cliente appena acquisito si presenta esattamente così — è un punto di partenza per la pulizia, non un elenco di errori."
            voci={gusciVuoti}
          />
        </div>
      )}

      <Table
        columns={clienteColumns}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="clienti"
        data={clienti}
        rowKey={(c) => c.id}
        isLoading={isLoading}
        emptyMessage="Nessun cliente trovato."
        onRowClick={openCliente}
      />

      <ClienteModal
        open={clienteModalOpen}
        onClose={() => setClienteModalOpen(false)}
        cliente={editingCliente}
        onSaved={() => loadClienti()}
      />

      <SchedaClienteAnteprima
        open={schedaOpen}
        onClose={() => setSchedaOpen(false)}
        onApplicata={(esito) => {
          const n = esito.campi_cliente.length;
          showToast(
            `Scheda applicata: ${n} camp${n === 1 ? "o" : "i"} compilat${n === 1 ? "o" : "i"}.`,
            "success"
          );
          loadClienti();
        }}
      />

      <ImportModal
        open={importClientiOpen}
        onClose={() => setImportClientiOpen(false)}
        onImported={() => loadClienti()}
        entityType="clienti"
        title="Importa clienti da file"
      />

      <ImportModal
        open={importSediOpen}
        onClose={() => setImportSediOpen(false)}
        onImported={() => loadClienti()}
        entityType="sedi"
        title="Importa sedi da file"
      />
      </div>
      )}
    </div>
  );
}

// ============================================================================
// Elenco richiudibile di contatti Odoo da ripulire.
//
// Chiuso di default e con l'altezza limitata perche' una delle due categorie e'
// grossa per natura — in sviluppo 425 gusci vuoti su 480 clienti — e stampata
// per intero spingerebbe la tabella dei clienti fuori dalla prima schermata a
// ogni sincronizzazione. Il conteggio sta nel `summary`, che e' l'informazione
// che serve sempre; i nomi si aprono quando si decide di lavorarci.
//
// Nessun pulsante di azione, a differenza del pannello dei doppioni: qui l'unica
// azione utile e' in Odoo.
// ============================================================================
function ElencoDaRipulire({
  titolo,
  descrizione,
  voci,
}: {
  titolo: string;
  descrizione: string;
  voci: { odoo_partner_id: number; nome: string }[];
}) {
  if (voci.length === 0) return null;
  return (
    <details className="mt-2 rounded-lg border border-line/60 px-3 py-2">
      <summary className="cursor-pointer text-sm font-semibold text-ink">{titolo}</summary>
      <p className="mt-1 text-xs text-ink/60">{descrizione}</p>
      <ul className="mt-2 max-h-60 overflow-y-auto text-xs text-ink/80">
        {voci.map((v) => (
          <li
            key={v.odoo_partner_id}
            className="flex items-baseline justify-between gap-3 border-b border-line/40 py-1 last:border-0"
          >
            <span>{v.nome}</span>
            <span className="shrink-0 tabular-nums text-ink/40">{v.odoo_partner_id}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

// ============================================================================
// Modal — crea/modifica cliente
// ============================================================================
/**
 * I campi modificabili dalla scheda cliente.
 *
 * Il blocco camerale (forma giuridica, REA, ATECO, attività) e quello del
 * legale rappresentante NON erano qui pur esistendo da sempre nella tabella
 * `clienti`: si popolavano solo per import o sincronizzazione, e a mano non si
 * potevano né correggere né inserire. Sono esattamente i segnaposto che il
 * mail-merge del Contratto di Cash Management andrà a leggere («Rappresentante_»,
 * «in_Qualità_di», «Cod_Cliente», «PEC»), quindi lasciarli non modificabili
 * avrebbe voluto dire un contratto con dei buchi non colmabili dall'app.
 */
type ClienteFormValues = {
  nome: string;
  insegna: string;
  tipo_contatto: "cliente" | "fornitore" | "altro";
  codice_cliente: string;
  partita_iva: string;
  codice_sdi: string;
  pec: string;
  pec_fatturazione: string;
  forma_giuridica: string;
  numero_rea: string;
  codice_ateco: string;
  descrizione_attivita: string;
  rappresentante_nome: string;
  rappresentante_qualita: string;
  indirizzo: string;
  civico: string;
  comune: string;
  cap: string;
  provincia: string;
  email: string;
  telefono: string;
  referente_nome: string;
  referente_email: string;
  referente_telefono: string;
  note: string;
};

function ClienteModal({
  open,
  onClose,
  cliente,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  cliente: Cliente | null;
  onSaved: (cliente: Cliente) => void;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ClienteFormValues>();

  useEffect(() => {
    if (open) {
      reset({
        nome: cliente?.nome ?? "",
        insegna: cliente?.insegna ?? "",
        tipo_contatto: cliente?.tipo_contatto ?? "cliente",
        codice_cliente: cliente?.codice_cliente ?? "",
        partita_iva: cliente?.partita_iva ?? "",
        codice_sdi: cliente?.codice_sdi ?? "",
        pec: cliente?.pec ?? "",
        pec_fatturazione: cliente?.pec_fatturazione ?? "",
        forma_giuridica: cliente?.forma_giuridica ?? "",
        numero_rea: cliente?.numero_rea ?? "",
        codice_ateco: cliente?.codice_ateco ?? "",
        descrizione_attivita: cliente?.descrizione_attivita ?? "",
        rappresentante_nome: cliente?.rappresentante_nome ?? "",
        rappresentante_qualita: cliente?.rappresentante_qualita ?? "",
        indirizzo: cliente?.indirizzo ?? "",
        civico: cliente?.civico ?? "",
        comune: cliente?.comune ?? "",
        cap: cliente?.cap ?? "",
        provincia: cliente?.provincia ?? "",
        email: cliente?.email ?? "",
        telefono: cliente?.telefono ?? "",
        referente_nome: cliente?.referente_nome ?? "",
        referente_email: cliente?.referente_email ?? "",
        referente_telefono: cliente?.referente_telefono ?? "",
        note: cliente?.note ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cliente]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const { cliente: saved } = cliente
        ? await clientsApi.updateCliente(cliente.id, values)
        : await clientsApi.createCliente(values);
      showToast(cliente ? "Cliente aggiornato." : "Cliente creato.", "success");
      onClose();
      onSaved(saved);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il cliente."), "error");
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cliente ? "Modifica cliente" : "Nuovo cliente"}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="cliente-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="cliente-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome / Ragione sociale"
          required
          error={errors.nome?.message}
          {...register("nome", { required: "Il nome è obbligatorio." })}
        />
        <Input label="Insegna" {...register("insegna")} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Tipo contatto</label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("tipo_contatto")}
          >
            <option value="cliente">Cliente</option>
            <option value="fornitore">Fornitore</option>
            <option value="altro">Altro</option>
          </select>
          <p className="text-xs text-ink/50">
            Per Centro Assistenza o Istituto di Vigilanza usa "Converti in…" nella scheda del contatto invece di
            scegliere qui — sono record separati, con utenti e permessi propri.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Partita IVA" {...register("partita_iva")} />
          {/* Il «Cod_Cliente» del contratto (es. 200D39/6321): è il codice con
              cui il cliente è identificato sui documenti contrattuali, distinto
              dagli altri codici interni già presenti in anagrafica. */}
          <Input label="Codice cliente" {...register("codice_cliente")} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Codice SDI" {...register("codice_sdi")} />
          <Input label="PEC fatturazione" {...register("pec_fatturazione")} />
        </div>
        <Input label="PEC" {...register("pec")} />

        {/* --- Dati camerali ---------------------------------------------
            Si compilano leggendo la visura camerale caricata nel fascicolo del
            cliente (riquadro "Documenti del cliente" nella scheda): la visura
            copre forma giuridica, REA, ATECO e attività, che sono proprio
            questi quattro. Nessuna lettura automatica del PDF — si trascrivono
            a mano, per scelta. */}
        <fieldset className="rounded-lg border border-line p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink/50">Dati camerali</legend>
          <div className="flex flex-col gap-3">
            <Input
              label="Forma giuridica"
              placeholder="es. impresa individuale, s.r.l."
              {...register("forma_giuridica")}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Numero REA" {...register("numero_rea")} />
              <Input label="Codice ATECO" {...register("codice_ateco")} />
            </div>
            <Input label="Tipo attività" {...register("descrizione_attivita")} />
          </div>
        </fieldset>

        {/* --- Legale rappresentante -------------------------------------
            «Rappresentante_» e «in_Qualità_di» nel contratto: chi firma per il
            cliente, e a che titolo. */}
        <fieldset className="rounded-lg border border-line p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink/50">
            Legale rappresentante
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Nome e cognome" {...register("rappresentante_nome")} />
            <Input
              label="In qualità di"
              placeholder="es. Amministratore Unico"
              {...register("rappresentante_qualita")}
            />
          </div>
        </fieldset>

        <Input label="Indirizzo" {...register("indirizzo")} />
        <div className="grid grid-cols-3 gap-3">
          <Input label="Civico" {...register("civico")} />
          <div className="col-span-2">
            <Input label="Comune" {...register("comune")} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="CAP" {...register("cap")} />
          <Input label="Provincia" {...register("provincia")} maxLength={2} className="uppercase" />
        </div>

        <Input label="Email" type="email" {...register("email")} />
        <Input label="Telefono" {...register("telefono")} />
        <Input label="Referente" {...register("referente_nome")} />
        <Input label="Email referente" type="email" {...register("referente_email")} />
        <Input label="Telefono referente" {...register("referente_telefono")} />
      </form>
    </Modal>
  );
}

// ============================================================================
// Modal — crea/modifica sede
// ============================================================================
type SedeFormValues = {
  nome_sede: string;
  indirizzo: string;
  citta: string;
  cap: string;
  provincia: string;
  email: string;
  telefono: string;
  /**
   * Coordinate di accredito DI QUESTO punto vendita. Lasciarle vuote non è un
   * campo mancante: significa "usa quelle dell'azienda", ed è il caso normale.
   * La scheda Sede mostra quale IBAN sta valendo davvero.
   */
  iban_accredito: string;
  banca_accredito: string;
  agenzia_accredito: string;
};

function SedeModal({
  open,
  onClose,
  clienteId,
  sede,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  clienteId: string;
  sede: SedeCliente | null;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [spostaOpen, setSpostaOpen] = useState(false);
  const [nuovaAziendaNome, setNuovaAziendaNome] = useState("");
  const [nuovaAziendaId, setNuovaAziendaId] = useState("");
  const [isSpostando, setIsSpostando] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SedeFormValues>();

  useEffect(() => {
    if (open) {
      reset({
        nome_sede: sede?.nome_sede ?? "",
        indirizzo: sede?.indirizzo ?? "",
        citta: sede?.citta ?? "",
        cap: sede?.cap ?? "",
        provincia: sede?.provincia ?? "",
        email: sede?.email ?? "",
        telefono: sede?.telefono ?? "",
        iban_accredito: sede?.iban_accredito ?? "",
        banca_accredito: sede?.banca_accredito ?? "",
        agenzia_accredito: sede?.agenzia_accredito ?? "",
      });
      setSpostaOpen(false);
      setNuovaAziendaNome("");
      setNuovaAziendaId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sede]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (sede) {
        await clientsApi.updateSede(sede.id, values);
      } else {
        await clientsApi.createSede({ ...values, cliente_id: clienteId });
      }
      showToast(sede ? "Sede aggiornata." : "Sede creata.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare la sede."), "error");
    }
  });

  async function handleDelete() {
    if (!sede) return;
    const confirmed = window.confirm(`Eliminare la sede "${sede.nome_sede ?? "senza nome"}"?`);
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await clientsApi.deleteSede(sede.id);
      showToast("Sede eliminata.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare la sede."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleSposta() {
    if (!sede || !nuovaAziendaId) return;
    const confirmed = window.confirm(
      `Spostare "${sede.nome_sede ?? "questa sede"}" sotto "${nuovaAziendaNome}"? Tutti gli interventi e le installazioni collegati a questa sede seguiranno automaticamente la nuova azienda.`
    );
    if (!confirmed) return;
    setIsSpostando(true);
    try {
      await clientsApi.spostaSede(sede.id, nuovaAziendaId);
      showToast(`Sede spostata sotto "${nuovaAziendaNome}".`, "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile spostare la sede."), "error");
    } finally {
      setIsSpostando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sede ? "Modifica sede" : "Nuova sede"}
      footer={
        <>
          {sede && (
            <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete}>
              Elimina
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="sede-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="sede-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome sede"
          required
          error={errors.nome_sede?.message}
          {...register("nome_sede", { required: "Il nome della sede è obbligatorio." })}
        />
        <Input label="Indirizzo" {...register("indirizzo")} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Input label="Città" {...register("citta")} />
          </div>
          <Input label="Prov." {...register("provincia")} maxLength={2} className="uppercase" />
        </div>
        <Input label="CAP" {...register("cap")} />
        <Input label="Email" type="email" {...register("email")} />
        <Input label="Telefono" {...register("telefono")} />

        {/* --- Accredito ---------------------------------------------------
            L'IBAN sta sul punto vendita perché è lì che lo tiene la Scheda
            Cliente reale: un esercente con più negozi può accreditarne uno per
            negozio. Chi ne ha uno solo lascia vuoto e vale quello dell'azienda,
            così resta un posto solo da correggere quando la banca cambia. */}
        <fieldset className="rounded-lg border border-line p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink/50">
            Accredito di questa sede
          </legend>
          <div className="flex flex-col gap-3">
            {/* Su una sede a conto interno l'IBAN è vuoto di proposito: salvarne
                uno fa passare la sede a IBAN (il backend lo valida). Si dice
                prima, perché il form da solo non lo mostrerebbe. */}
            {sede?.accredito_tipo === "conto_interno" && (
              <p role="note" className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-xs text-ink/70">
                Questa sede accredita sul conto interno{" "}
                <span className="font-mono">
                  {sede.accredito_conto_interno ? `…${sede.accredito_conto_interno.replace(/\s+/g, "").slice(-4)}` : "(numero non indicato)"}
                </span>
                {sede.accredito_origine === "aznext" ? ", importato da AZNext" : ""}. Se inserisci un IBAN valido, l&apos;accredito
                passa all&apos;IBAN e il conto interno viene tolto.
              </p>
            )}
            <Input label="IBAN" {...register("iban_accredito")} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Banca" {...register("banca_accredito")} />
              <Input label="Agenzia / filiale" {...register("agenzia_accredito")} />
            </div>
            <p className="text-xs text-ink/50">
              Lascia vuoto se questa sede usa il conto dell&apos;azienda: verrà ereditato automaticamente.
            </p>
          </div>
        </fieldset>

        {sede && (
          <div className="border-t border-line pt-4">
            {!spostaOpen ? (
              <Button type="button" variant="ghost" onClick={() => setSpostaOpen(true)}>
                Sposta sotto un'altra azienda
              </Button>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border border-dashed border-accent/40 bg-accent/5 p-3">
                <ClienteAutocomplete
                  label="Nuova azienda madre"
                  value={nuovaAziendaNome}
                  onChange={setNuovaAziendaNome}
                  onSelect={(c) => setNuovaAziendaId(c.id)}
                />
                <p className="text-xs text-ink/50">
                  Interventi e installazioni già collegati a questa sede seguiranno automaticamente la nuova azienda.
                </p>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSpostaOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="button" size="sm" loading={isSpostando} disabled={!nuovaAziendaId} onClick={handleSposta}>
                    Conferma spostamento
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ============================================================================
// Scheda — Centri Assistenza (elenco mancante prima, richiesto esplicitamente)
// ============================================================================
function CentriAssistenzaTab() {
  const { showToast } = useToast();
  const [centri, setCentri] = useState<CentroAssistenza[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CentroAssistenza | null>(null);

  async function load() {
    setIsLoading(true);
    try {
      const { centri: list } = await anagraficaApi.listCentriAssistenza();
      setCentri(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i centri assistenza."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: TableColumn<CentroAssistenza>[] = [
    { key: "nome", header: "Nome" },
    { key: "abbreviazione", header: "Sigla", render: (c) => c.abbreviazione ?? "—" },
    { key: "comune", header: "Comune", render: (c) => c.comune ?? "—" },
    { key: "telefono", header: "Telefono", render: (c) => c.telefono ?? "—" },
    { key: "email", header: "Email", render: (c) => c.email ?? "—" },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Centri Assistenza</h1>
          <p className="mt-1 text-sm text-ink/50">Anagrafica dei centri assistenza collegati alla piattaforma.</p>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuovo centro
        </Button>
      </header>

      <Table
        columns={columns}
        data={centri}
        rowKey={(c) => c.id}
        isLoading={isLoading}
        emptyMessage="Nessun centro assistenza registrato."
        onRowClick={(c) => { setEditing(c); setModalOpen(true); }}
      />

      <CentroAssistenzaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        centro={editing}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
    </div>
  );
}

type CentroFormValues = {
  nome: string;
  abbreviazione: string;
  email: string;
  telefono: string;
  indirizzo: string;
  comune: string;
  provincia: string;
  cap: string;
  partita_iva: string;
  note: string;
};

function CentroAssistenzaModal({
  open,
  onClose,
  centro,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  centro: CentroAssistenza | null;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CentroFormValues>();

  useEffect(() => {
    if (open) {
      reset({
        nome: centro?.nome ?? "",
        abbreviazione: centro?.abbreviazione ?? "",
        email: centro?.email ?? "",
        telefono: centro?.telefono ?? "",
        indirizzo: centro?.indirizzo ?? "",
        comune: centro?.comune ?? "",
        provincia: centro?.provincia ?? "",
        cap: centro?.cap ?? "",
        partita_iva: centro?.partita_iva ?? "",
        note: centro?.note ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, centro]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (centro) {
        await anagraficaApi.updateCentroAssistenza(centro.id, values);
      } else {
        await anagraficaApi.createCentroAssistenza(values);
      }
      showToast(centro ? "Centro aggiornato." : "Centro creato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il centro."), "error");
    }
  });

  async function handleDelete() {
    if (!centro) return;
    const confirmed = window.confirm(`Eliminare "${centro.nome}"? Bloccato automaticamente se ha utenti o dati collegati.`);
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await anagraficaApi.deleteCentroAssistenza(centro.id);
      showToast("Centro eliminato.", "success");
      onClose();
      onDeleted();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare — probabilmente ha dati collegati."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={centro ? "Modifica centro assistenza" : "Nuovo centro assistenza"}
      footer={
        <>
          {centro && (
            <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete}>
              Elimina
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="centro-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="centro-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome"
          required
          error={errors.nome?.message}
          {...register("nome", { required: "Il nome è obbligatorio." })}
        />
        <Input label="Sigla" {...register("abbreviazione")} />
        <Input label="Email" type="email" {...register("email")} />
        <Input label="Telefono" {...register("telefono")} />
        <Input label="Indirizzo" {...register("indirizzo")} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Input label="Comune" {...register("comune")} />
          </div>
          <Input label="Prov." {...register("provincia")} maxLength={2} className="uppercase" />
        </div>
        <Input label="CAP" {...register("cap")} />
        <Input label="Partita IVA" {...register("partita_iva")} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note</label>
          <textarea
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("note")}
          />
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// Scheda — Operatori logistici (trasporto valori e sala conta)
//
// Un'anagrafica sola, mostrata come DUE elenchi: e' cosi' che questi soggetti
// vengono nominati sul campo ("il trasportatore", "la sala conta"), e un
// elenco unico costringerebbe a leggere due flag per capire cosa si sta
// guardando. Sotto pero' la riga e' una: la stessa azienda che fa entrambi i
// mestieri compare in tutti e due i sottoelenchi senza essere duplicata, e
// modificarla da un lato la aggiorna anche dall'altro.
// ============================================================================
function OperatoriLogisticiTab() {
  const { showToast } = useToast();
  const [operatori, setOperatori] = useState<OperatoreLogistico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OperatoreLogistico | null>(null);
  // Nell'URL come la tab che lo contiene: `?tab=operatori&elenco=sala_conta`.
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const elenco: CapacitaOperatore = searchParams.get("elenco") === "sala_conta" ? "sala_conta" : "trasporto_valori";
  const setElenco = (e: CapacitaOperatore) => scrivi({ elenco: e === "trasporto_valori" ? null : e });

  // Si carica l'anagrafica INTERA una volta e si filtra qui, invece di
  // richiamare l'API a ogni cambio di elenco: sono poche decine di righe, e
  // cosi' passare da un elenco all'altro non fa sfarfallare la tabella.
  async function load() {
    setIsLoading(true);
    try {
      const { operatori: list } = await anagraficaApi.listOperatoriLogistici();
      setOperatori(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare gli operatori logistici."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibili = operatori.filter((o) =>
    elenco === "trasporto_valori" ? o.fa_trasporto_valori : o.fa_sala_conta
  );

  // Chi non ha ancora nessuna delle due spunte non compare in nessuno dei due
  // elenchi: senza dirlo sembrerebbe sparito dopo il salvataggio.
  const senzaMestiere = operatori.filter((o) => !o.fa_trasporto_valori && !o.fa_sala_conta);

  const columns: TableColumn<OperatoreLogistico>[] = [
    { key: "nome", header: "Nome" },
    {
      key: "capacita",
      header: "Fa anche",
      render: (o) =>
        elenco === "trasporto_valori"
          ? o.fa_sala_conta
            ? "Sala conta"
            : "—"
          : o.fa_trasporto_valori
            ? "Trasporto valori"
            : "—",
    },
    { key: "zona", header: "Zona", render: (o) => o.zona ?? "—" },
    { key: "comune", header: "Comune", render: (o) => o.comune ?? "—" },
    { key: "telefono", header: "Telefono", render: (o) => o.telefono ?? "—" },
    { key: "email", header: "Email", render: (o) => o.email ?? "—" },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Operatori logistici</h1>
          <p className="mt-1 text-sm text-ink/50">
            Chi ritira e trasporta il contante e chi lo conta, per i punti con servizio precredit.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuovo operatore
        </Button>
      </header>

      <div className="mb-4 flex gap-1.5 rounded-lg bg-black/5 p-1 w-fit">
        {(
          [
            ["trasporto_valori", "Trasporto valori"],
            ["sala_conta", "Sala conta"],
          ] as [CapacitaOperatore, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setElenco(key)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
              elenco === key ? "bg-white text-ink shadow-sm" : "text-ink/50 hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={visibili}
        rowKey={(o) => o.id}
        isLoading={isLoading}
        emptyMessage={
          elenco === "trasporto_valori"
            ? "Nessuna societa' di trasporto valori registrata."
            : "Nessuna sala conta registrata."
        }
        onRowClick={(o) => { setEditing(o); setModalOpen(true); }}
      />

      {!isLoading && senzaMestiere.length > 0 && (
        <p className="mt-3 text-xs text-ink/40">
          {senzaMestiere.length}{" "}
          {senzaMestiere.length === 1 ? "operatore registrato non ha" : "operatori registrati non hanno"} nessuna delle
          due spunte, quindi {senzaMestiere.length === 1 ? "non compare" : "non compaiono"} in questi elenchi:{" "}
          {senzaMestiere.map((o) => o.nome).join(", ")}.
        </p>
      )}

      <OperatoreLogisticoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        operatore={editing}
        capacitaIniziale={elenco}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
    </div>
  );
}

type OperatoreFormValues = {
  nome: string;
  fa_trasporto_valori: boolean;
  fa_sala_conta: boolean;
  zona: string;
  email: string;
  telefono: string;
  indirizzo: string;
  comune: string;
  provincia: string;
  cap: string;
  partita_iva: string;
  note: string;
};

function OperatoreLogisticoModal({
  open,
  onClose,
  operatore,
  capacitaIniziale,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  operatore: OperatoreLogistico | null;
  /** su un operatore NUOVO parte spuntato l'elenco da cui si e' premuto il bottone */
  capacitaIniziale: CapacitaOperatore;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OperatoreFormValues>();

  useEffect(() => {
    if (open) {
      reset({
        nome: operatore?.nome ?? "",
        fa_trasporto_valori: operatore?.fa_trasporto_valori ?? capacitaIniziale === "trasporto_valori",
        fa_sala_conta: operatore?.fa_sala_conta ?? capacitaIniziale === "sala_conta",
        zona: operatore?.zona ?? "",
        email: operatore?.email ?? "",
        telefono: operatore?.telefono ?? "",
        indirizzo: operatore?.indirizzo ?? "",
        comune: operatore?.comune ?? "",
        provincia: operatore?.provincia ?? "",
        cap: operatore?.cap ?? "",
        partita_iva: operatore?.partita_iva ?? "",
        note: operatore?.note ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, operatore, capacitaIniziale]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (operatore) {
        await anagraficaApi.updateOperatoreLogistico(operatore.id, values);
      } else {
        await anagraficaApi.createOperatoreLogistico(values);
      }
      showToast(operatore ? "Operatore aggiornato." : "Operatore creato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'operatore."), "error");
    }
  });

  async function handleDelete() {
    if (!operatore) return;
    const confirmed = window.confirm(
      `Eliminare "${operatore.nome}"? Bloccato automaticamente se e' collegato a dei punti.`
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await anagraficaApi.deleteOperatoreLogistico(operatore.id);
      showToast("Operatore eliminato.", "success");
      onClose();
      onDeleted();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare — probabilmente e' collegato a dei punti."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={operatore ? "Modifica operatore logistico" : "Nuovo operatore logistico"}
      footer={
        <>
          {operatore && (
            <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete}>
              Elimina
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="operatore-logistico-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="operatore-logistico-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome"
          required
          error={errors.nome?.message}
          {...register("nome", { required: "Il nome è obbligatorio." })}
        />

        {/* Le due spunte sono indipendenti apposta: la stessa azienda puo'
            fare entrambi i mestieri, ed e' il motivo per cui l'anagrafica e'
            una sola invece di due gemelle. */}
        <div className="flex flex-col gap-2 rounded-lg border border-line bg-ink/[0.02] px-3 py-2.5">
          <span className="text-sm font-medium text-ink">In quali elenchi compare</span>
          <label className="flex items-center gap-2 text-sm text-ink/70">
            <input type="checkbox" {...register("fa_trasporto_valori")} />
            Trasporto valori
          </label>
          <label className="flex items-center gap-2 text-sm text-ink/70">
            <input type="checkbox" {...register("fa_sala_conta")} />
            Sala conta
          </label>
        </div>

        <Input label="Zona" {...register("zona")} />
        <Input label="Email" type="email" {...register("email")} />
        <Input label="Telefono" {...register("telefono")} />
        <Input label="Indirizzo" {...register("indirizzo")} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Input label="Comune" {...register("comune")} />
          </div>
          <Input label="Prov." {...register("provincia")} maxLength={2} className="uppercase" />
        </div>
        <Input label="CAP" {...register("cap")} />
        <Input label="Partita IVA" {...register("partita_iva")} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note</label>
          <textarea
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("note")}
          />
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// Scheda — Istituti di Vigilanza (stesso pattern di sopra)
// ============================================================================
function IstitutiVigilanzaTab() {
  const { showToast } = useToast();
  const [istituti, setIstituti] = useState<IstitutoVigilanza[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IstitutoVigilanza | null>(null);

  async function load() {
    setIsLoading(true);
    try {
      const { istituti: list } = await anagraficaApi.listIstitutiVigilanza();
      setIstituti(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare gli istituti di vigilanza."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: TableColumn<IstitutoVigilanza>[] = [
    { key: "nome", header: "Nome" },
    { key: "zona", header: "Zona", render: (i) => i.zona ?? "—" },
    { key: "comune", header: "Comune", render: (i) => i.comune ?? "—" },
    { key: "telefono", header: "Telefono", render: (i) => i.telefono ?? "—" },
    { key: "email", header: "Email", render: (i) => i.email ?? "—" },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Istituti di Vigilanza</h1>
          <p className="mt-1 text-sm text-ink/50">Anagrafica degli istituti di vigilanza collegati alla piattaforma.</p>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuovo istituto
        </Button>
      </header>

      <Table
        columns={columns}
        data={istituti}
        rowKey={(i) => i.id}
        isLoading={isLoading}
        emptyMessage="Nessun istituto di vigilanza registrato."
        onRowClick={(i) => { setEditing(i); setModalOpen(true); }}
      />

      <IstitutoVigilanzaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        istituto={editing}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
    </div>
  );
}

type IstitutoFormValues = {
  nome: string;
  zona: string;
  email: string;
  telefono: string;
  indirizzo: string;
  comune: string;
  provincia: string;
  cap: string;
  partita_iva: string;
  note: string;
};

function IstitutoVigilanzaModal({
  open,
  onClose,
  istituto,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  istituto: IstitutoVigilanza | null;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IstitutoFormValues>();

  useEffect(() => {
    if (open) {
      reset({
        nome: istituto?.nome ?? "",
        zona: istituto?.zona ?? "",
        email: istituto?.email ?? "",
        telefono: istituto?.telefono ?? "",
        indirizzo: istituto?.indirizzo ?? "",
        comune: istituto?.comune ?? "",
        provincia: istituto?.provincia ?? "",
        cap: istituto?.cap ?? "",
        partita_iva: istituto?.partita_iva ?? "",
        note: istituto?.note ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, istituto]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (istituto) {
        await anagraficaApi.updateIstitutoVigilanza(istituto.id, values);
      } else {
        await anagraficaApi.createIstitutoVigilanza(values);
      }
      showToast(istituto ? "Istituto aggiornato." : "Istituto creato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'istituto."), "error");
    }
  });

  async function handleDelete() {
    if (!istituto) return;
    const confirmed = window.confirm(`Eliminare "${istituto.nome}"? Bloccato automaticamente se ha utenti o dati collegati.`);
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await anagraficaApi.deleteIstitutoVigilanza(istituto.id);
      showToast("Istituto eliminato.", "success");
      onClose();
      onDeleted();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare — probabilmente ha dati collegati."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={istituto ? "Modifica istituto di vigilanza" : "Nuovo istituto di vigilanza"}
      footer={
        <>
          {istituto && (
            <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete}>
              Elimina
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="istituto-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="istituto-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Nome"
          required
          error={errors.nome?.message}
          {...register("nome", { required: "Il nome è obbligatorio." })}
        />
        <Input label="Zona" {...register("zona")} />
        <Input label="Email" type="email" {...register("email")} />
        <Input label="Telefono" {...register("telefono")} />
        <Input label="Indirizzo" {...register("indirizzo")} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Input label="Comune" {...register("comune")} />
          </div>
          <Input label="Prov." {...register("provincia")} maxLength={2} className="uppercase" />
        </div>
        <Input label="CAP" {...register("cap")} />
        <Input label="Partita IVA" {...register("partita_iva")} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note</label>
          <textarea
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("note")}
          />
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// Documenti del cliente — il fascicolo camerale (visura e simili).
//
// Riquadro distinto da "Foto e documenti" (MediaUploader) che sta accanto, e
// non un doppione: quello raccoglie foto del negozio e allegati generici, qui
// stanno i documenti con un TIPO e una DATA DI EMISSIONE. La domanda a cui
// questo riquadro risponde è "di questo cliente ho una visura, ed è
// abbastanza recente?" — non esprimibile su un allegato generico.
//
// La visura si legge a mano: nessun parsing del PDF, per scelta. I campi che
// copre (forma giuridica, REA, ATECO, attività, PEC) si trascrivono nella
// scheda cliente guardandola.
// ============================================================================

const TIPO_DOCUMENTO_LABEL: Record<clientsApi.TipoDocumentoCliente, string> = {
  visura_camerale: "Visura camerale",
  scheda_cliente_compilata: "Scheda Cliente compilata",
  altro: "Altro",
};

/**
 * Il testo del pulsante segue il TIPO, perché segue il formato.
 *
 * «Carica PDF» su un tipo che vuole un `.xlsx` manderebbe l'utente a cercare il
 * file sbagliato, e il backend lo respingerebbe con un 400 dopo il caricamento.
 */
const ETICHETTA_CARICA: Record<clientsApi.TipoDocumentoCliente, string> = {
  visura_camerale: "Carica PDF",
  scheda_cliente_compilata: "Carica la Scheda (.xlsx)",
  altro: "Carica PDF",
};

function DocumentiClientePanel({ clienteId }: { clienteId: string }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [documenti, setDocumenti] = useState<clientsApi.DocumentoCliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const [tipo, setTipo] = useState<clientsApi.TipoDocumentoCliente>("visura_camerale");
  const [dataDocumento, setDataDocumento] = useState("");
  const [esitoScheda, setEsitoScheda] = useState<clientsApi.EsitoScheda | null>(null);
  // Una Scheda Cliente non si applica più al primo click: passa dall'anteprima,
  // come il caricamento da Anagrafiche. Gli altri tipi di documento si
  // archiviano e basta — non c'è niente da anteprimare, perché non modificano
  // nessun campo.
  const [anteprimaSchedaOpen, setAnteprimaSchedaOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const puoCaricare = hasPermission("clienti_sedi:update");
  const puoEliminare = hasPermission("clienti_sedi:documenti_delete");

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    clientsApi
      .listDocumentiCliente(clienteId)
      .then((r) => {
        if (!annullato) setDocumenti(r.documenti);
      })
      .catch((err) => {
        if (!annullato) showToast(getErrorMessage(err, "Impossibile caricare i documenti."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    // (per la Scheda Cliente non si arriva qui: il pulsante apre l'anteprima)
    try {
      const { documento, scheda } = await clientsApi.uploadDocumentoCliente(
        clienteId,
        file,
        tipo,
        dataDocumento || undefined
      );
      // Il resoconto della lettura resta a schermo finché non si carica un altro
      // documento: e' l'unico posto in cui si vede che il modulo diceva una cosa
      // diversa da quella a sistema e che si e' tenuta quella a sistema.
      setEsitoScheda(scheda);
      // In testa: l'elenco è ordinato per data documento decrescente lato
      // server, e un documento appena caricato è quasi sempre il più recente.
      // Alla prossima apertura della scheda l'ordine vero arriva dal backend.
      setDocumenti((precedenti) => [documento, ...precedenti]);
      setDataDocumento("");
      if (scheda?.applicato) {
        const n = scheda.campi_cliente.length;
        const s = scheda.sedi.filter((x) => x.sede_id).length;
        showToast(
          `Scheda letta: ${n} camp${n === 1 ? "o" : "i"} compilat${n === 1 ? "o" : "i"}, ` +
            `${s} punt${s === 1 ? "o" : "i"} vendita agganciat${s === 1 ? "o" : "i"}.`,
          "success"
        );
      } else {
        showToast("Documento caricato.", "success");
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il documento."), "error");
    } finally {
      setIsUploading(false);
      // Azzerato sempre: senza, riselezionare lo STESSO file non farebbe
      // scattare onChange e il secondo tentativo sembrerebbe ignorato.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDownload(doc: clientsApi.DocumentoCliente) {
    try {
      await filesApi.downloadProtectedFile(doc.file_url, doc.nome_file ?? "documento.pdf");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scaricare il documento."), "error");
    }
  }

  async function handleDelete(doc: clientsApi.DocumentoCliente) {
    const confermato = window.confirm(
      `Eliminare "${doc.nome_file ?? "questo documento"}"? Il file viene cancellato e non è recuperabile.`
    );
    if (!confermato) return;
    setEliminandoId(doc.id);
    try {
      await clientsApi.deleteDocumentoCliente(doc.id);
      setDocumenti((precedenti) => precedenti.filter((d) => d.id !== doc.id));
      showToast("Documento eliminato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il documento."), "error");
    } finally {
      setEliminandoId(null);
    }
  }

  return (
    <div className="mt-6 rounded-card border border-line bg-white p-4 shadow-card">
      <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">
        Documenti del cliente{" "}
        <span className="font-normal normal-case text-ink/40">
          — visura camerale (PDF) e Scheda Cliente compilata (.xlsx)
        </span>
      </p>

      {puoCaricare && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-line p-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="doc-tipo" className="text-xs text-ink/60">
              Tipo
            </label>
            <select
              id="doc-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as clientsApi.TipoDocumentoCliente)}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            >
              {(Object.keys(TIPO_DOCUMENTO_LABEL) as clientsApi.TipoDocumentoCliente[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_DOCUMENTO_LABEL[t]}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Data del documento"
            type="date"
            value={dataDocumento}
            onChange={(e) => setDataDocumento(e.target.value)}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept={clientsApi.MIME_PER_TIPO[tipo]}
            className="hidden"
            onChange={handleFile}
          />
          <Button
            type="button"
            variant="secondary"
            loading={isUploading}
            onClick={() =>
              // La Scheda Cliente scrive sull'anagrafica, quindi passa
              // dall'anteprima: il file lo sceglie quella. Gli altri tipi si
              // archiviano soltanto, e vanno dritti al selettore.
              tipo === "scheda_cliente_compilata"
                ? setAnteprimaSchedaOpen(true)
                : fileInputRef.current?.click()
            }
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {ETICHETTA_CARICA[tipo]}
          </Button>

          <SchedaClienteAnteprima
            open={anteprimaSchedaOpen}
            onClose={() => setAnteprimaSchedaOpen(false)}
            clienteId={clienteId}
            onApplicata={(esito) => {
              setEsitoScheda(esito);
              const n = esito.campi_cliente.length;
              const sedi = esito.sedi.filter((x) => x.sede_id).length;
              showToast(
                `Scheda applicata: ${n} camp${n === 1 ? "o" : "i"} compilat${n === 1 ? "o" : "i"}, ` +
                  `${sedi} punt${sedi === 1 ? "o" : "i"} vendita agganciat${sedi === 1 ? "o" : "i"}.`,
                "success"
              );
              // L'elenco dei documenti va riletto: la conferma ne ha archiviato
              // uno nuovo, e da qui non abbiamo la riga che lo descrive.
              clientsApi
                .listDocumentiCliente(clienteId)
                .then(({ documenti }) => setDocumenti(documenti))
                .catch(() => undefined);
            }}
          />

          {/* La data è di emissione, non di caricamento: è quella che dice se
              la visura è ancora buona. Lasciarla vuota è ammesso, ma il
              documento finirà in fondo all'elenco. */}
          <p className="w-full text-xs text-ink/45">
            La data è quella stampata sul documento (per una visura, la data di emissione), non quella di oggi.
          </p>
        </div>
      )}

      {/* IL RESOCONTO DELLA LETTURA, solo dopo una Scheda Cliente.
          Serve perché la regola del backend è «riempi solo ciò che è vuoto»:
          senza mostrarlo, un campo che il modulo dice diverso resterebbe
          com'era e nessuno saprebbe che il modulo diceva altro. È la stessa
          ragione per cui quei campi finiscono anche nel registro attività. */}
      {esitoScheda && (
        <div className="mb-4 rounded-lg border border-line bg-surface p-3 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-ink">Cosa ha letto la Scheda</span>
            <button
              type="button"
              onClick={() => setEsitoScheda(null)}
              className="text-xs text-ink/45 transition-colors hover:text-ink"
            >
              Chiudi
            </button>
          </div>

          {esitoScheda.campi_cliente.length > 0 ? (
            <p className="text-ink/70">
              {esitoScheda.campi_cliente.length === 1 ? (
                <>Compilato <strong>1</strong> campo dell&apos;anagrafica che era vuoto.</>
              ) : (
                <>
                  Compilati <strong>{esitoScheda.campi_cliente.length}</strong> campi dell&apos;anagrafica che erano
                  vuoti.
                </>
              )}
            </p>
          ) : (
            <p className="text-ink/70">Nessun campo da compilare: l&apos;anagrafica era già completa.</p>
          )}

          {esitoScheda.campi_saltati.length > 0 && (
            <div className="mt-2">
              <p className="text-ink/70">
                {esitoScheda.campi_saltati.length === 1 ? (
                  <>
                    <strong>1</strong> campo del modulo dice altro e <strong>non</strong> è stato scritto — a sistema
                    resta il valore di prima:
                  </>
                ) : (
                  <>
                    <strong>{esitoScheda.campi_saltati.length}</strong> campi del modulo dicono altro e{" "}
                    <strong>non</strong> sono stati scritti — a sistema resta il valore di prima:
                  </>
                )}
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-ink/60">
                {esitoScheda.campi_saltati.map((c) => (
                  <li key={c.campo}>
                    <span className="font-medium text-ink/75">{clientsApi.etichettaCampo(c.campo)}</span>: resta «
                    {c.attuale}», nel modulo «{c.nel_modulo}»
                  </li>
                ))}
              </ul>
            </div>
          )}

          {esitoScheda.sedi.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-ink/60">
              {esitoScheda.sedi.map((s) => (
                <li key={s.foglio}>
                  <span className="font-medium text-ink/75">{s.foglio}</span>
                  {s.sede_id
                    ? ` → sede «${s.insegna ?? ""}», ${s.campi_riempiti.length} campi compilati`
                    : ` → nessuna sede corrisponde a «${s.insegna ?? "senza insegna"}»: non è stata creata`}
                </li>
              ))}
            </ul>
          )}

          {esitoScheda.avvisi.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-accent-dark">
              {esitoScheda.avvisi.map((a, i) => (
                <li key={i}>⚠ {a}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="py-4 text-center text-sm text-ink/50">Caricamento…</p>
      ) : documenti.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
          Nessun documento caricato per questo cliente.
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {documenti.map((doc) => (
            <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge color={doc.tipo === "visura_camerale" ? "primary" : "neutral"}>
                    {TIPO_DOCUMENTO_LABEL[doc.tipo]}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => handleDownload(doc)}
                    className="truncate text-sm font-semibold text-primary hover:underline"
                  >
                    {doc.nome_file ?? "documento.pdf"}
                  </button>
                </div>
                <p className="mt-0.5 text-xs text-ink/45">
                  {doc.data_documento
                    ? `Del ${new Date(doc.data_documento).toLocaleDateString("it-IT")}`
                    : "Senza data del documento"}
                  {" · "}
                  caricato il {new Date(doc.created_at).toLocaleDateString("it-IT")}
                  {doc.creatore?.full_name ? ` da ${doc.creatore.full_name}` : ""}
                </p>
              </div>

              {puoEliminare && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={eliminandoId === doc.id}
                  onClick={() => handleDelete(doc)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Elimina
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// ClienteAttivitaPanel — vista aggregata "tutto quello che riguarda questo
// cliente": installazioni + sopralluoghi + interventi + attivazioni collegati
// via cliente_id. Le righe rimandano al pannello del modulo (?open=id), come
// nel Calendario unificato. Le installazioni compaiono man mano che si
// completa il backfill cliente_id su installation_schedule.
// ============================================================================
function ClienteAttivitaPanel({ clienteId }: { clienteId: string }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState<clientsApi.ClienteAttivita | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    clientsApi
      .getClienteAttivita(clienteId)
      .then(setData)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare le attività collegate."), "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // `dataStimata`: la cascata `data_effettiva ?? ipotesi_data` collassa due
  // colonne in una e perde l'unica cosa che uno storico deve dire — se la cosa
  // è successa davvero. Vedi `components/ui/DataStorico.tsx` per la regola
  // completa, sopralluoghi compresi (che hanno un campo solo e si giudicano sul
  // calendario, non sul campo).
  const sezioni: { label: string; route: string; items: { id: string; titolo: string; data: string | null; dataStimata: boolean; daIpotesiData?: boolean; stato: string | null }[] }[] = data
    ? [
        {
          label: "Installazioni",
          route: "/schedule",
          items: data.installazioni.map((r) => ({ id: r.id, titolo: r.insegna ?? r.localita ?? "Installazione", data: r.data_effettiva ?? r.ipotesi_data, dataStimata: !r.data_effettiva, daIpotesiData: true, stato: r.stato })),
        },
        {
          label: "Sopralluoghi",
          route: "/sopralluoghi",
          items: data.sopralluoghi.map((r) => ({ id: r.id, titolo: r.ragione_sociale_cliente ?? r.insegna_cliente ?? "Sopralluogo", data: r.data_sopralluogo, dataStimata: isFutura(r.data_sopralluogo), stato: r.stato })),
        },
        {
          label: "Interventi",
          route: "/interventions",
          items: data.interventi.map((r) => ({ id: r.id, titolo: r.nome ?? r.cliente_nome ?? "Intervento", data: r.data_completamento ?? r.data_prevista, dataStimata: !r.data_completamento, stato: r.stato })),
        },
        {
          label: "Attivazioni",
          route: "/activations",
          items: data.attivazioni.map((r) => ({ id: r.id, titolo: r.cliente_nome ?? r.sede ?? "Attivazione", data: r.data_effettiva ?? r.data_attivazione, dataStimata: !r.data_effettiva, stato: r.stato })),
        },
      ]
    : [];

  return (
    <div className="mt-6 rounded-card border border-line bg-white p-4 shadow-card">
      <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">
        Attività collegate <span className="font-normal normal-case text-ink/40">— tutto ciò che riguarda questo cliente</span>
      </p>
      {loading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {sezioni.map((s) => (
            <div key={s.label} className="rounded-lg border border-line">
              <div className="flex items-center justify-between border-b border-line px-3 py-2">
                <span className="text-sm font-semibold text-ink">{s.label}</span>
                <span className="tabular-nums text-xs text-ink/50">{s.items.length}</span>
              </div>
              {s.items.length === 0 ? (
                <p className="px-3 py-2 text-xs text-ink/40">Nessun record collegato.</p>
              ) : (
                <ul className="max-h-52 overflow-y-auto">
                  {s.items.map((it) => (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`${s.route}?open=${it.id}`)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-primary/5"
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-1.5">
                          <span className="truncate font-medium text-ink">{it.titolo}</span>
                          <span className="text-ink/40">·</span>
                          <DataStorico data={it.data} stimata={it.dataStimata} daIpotesiData={it.daIpotesiData} stato={it.stato} className="shrink-0 !text-xs" />
                        </span>
                        {it.stato && <Badge stato={it.stato}>{it.stato}</Badge>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
